/* eslint max-lines: ["error", {"max": 350, "skipComments": true}] */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { autocompleteDemoItems } from './autocompleteDemoItems';
import { AutocompleteQueryItem } from './query/AutocompleteQueryItem';
import { AutocompleteLawyerItem } from './lawyer/AutocompleteLawyerItem';
import { AutocompleteEnterpriseItem } from './enterprise/AutocompleteEnterpriseItem';
import { AutocompleteLawFirmItem } from './law-firm/AutocompleteLawFirmItem';
import { AutocompleteLegislationItem } from './legislation/AutocompleteLegislationItem';
import { keyboardEvents } from '../keyboardEvents';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import {
  getLawyerUrl,
  getEnterpriseUrl,
  getLawFirmUrl,
} from './autocompleteUtils';
import { getLegislationUrl } from '../../../../legislation/utils/legislationUtils';
import { buildUrlWithParameters } from '../../../../utils/url-utils/urlUtils';
import {
  AutocompleteItemType,
  WrittenQueryItemType,
} from './searchAutocomplete.types';

type Props = {
  items: AutocompleteItemType[];
  query: string;
  isHistoryMode: boolean;
  isDemoMode: boolean;
  onItemSelected: Function;
  updateQuery: Function;
  manualClose: Function;
};

type State = {
  index: number;
  query: string;
};

function getSelectedItem(
  query: string,
  items: AutocompleteItemType[],
  index: number,
  isDemoMode: boolean,
): AutocompleteItemType | WrittenQueryItemType {
  const hasQuery = query.length > 0;
  const realItems = isDemoMode ? autocompleteDemoItems : items;

  if (hasQuery && index === 0) {
    return {
      label: query,
      suggestion: query,
      type: 'written_query',
    };
  }
  if (hasQuery) {
    return realItems[index - 1];
  }
  return realItems[index];
}

export class SearchAutocompleteList extends React.Component<Props, State> {
  state: State = {
    index: -1,
    query: '',
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    if (state.query !== props.query) {
      return {
        query: props.query,
        index: 0,
      };
    }
    return {
      query: props.query,
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyPress, false);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress, false);
  }

  handleKeyPress = (evt: KeyboardEvent) => {
    const { keyCode } = evt;
    const { items, query, updateQuery, isDemoMode } = this.props;
    const { index } = this.state;
    const hasQuery = query.length > 0;
    const itemsLength = isDemoMode
      ? autocompleteDemoItems.length
      : items.length + (hasQuery ? 1 : 0);
    let newIndex = index;

    if (
      items.length === 0 &&
      [keyboardEvents.upArrow, keyboardEvents.downArrow].includes(keyCode)
    ) {
      /*
       Let browser native behavior when no suggestions :
       up arrow -> go to end of input
       bottom arrow -> go to the beginning
      */
      return;
    }

    if (keyCode === keyboardEvents.upArrow) {
      evt.preventDefault();
      newIndex = index > 0 ? index - 1 : itemsLength - 1;
      this.setState({ index: newIndex });
      const item = getSelectedItem(query, items, newIndex, isDemoMode);
      updateQuery(item.label, { position: newIndex, items, type: item.type });
    } else if (keyCode === keyboardEvents.downArrow) {
      evt.preventDefault();
      newIndex = index < itemsLength - 1 ? index + 1 : 0;
      this.setState({ index: newIndex });
      const item = getSelectedItem(query, items, newIndex, isDemoMode);
      updateQuery(item.label, { position: newIndex, items, type: item.type });
    } else if (
      keyCode === keyboardEvents.tab ||
      keyCode === keyboardEvents.enter
    ) {
      const item = getSelectedItem(query, items, index, isDemoMode);
      if (item) {
        this.selectItemFromKeyboard(item);
      }
    }
  };

  onMouseSelect = (index: number) => () => {
    const { items, query, isDemoMode } = this.props;

    const item = getSelectedItem(query, items, index, isDemoMode);
    this.selectItem(item, 'autocomplete_click');
  };

  selectItemFromKeyboard = (
    item: AutocompleteItemType | WrittenQueryItemType,
  ) => {
    if (item.type === 'lawyer') {
      window.location.href = buildUrlWithParameters(getLawyerUrl(item), {
        source: 'autocomplete_result',
      });
      return;
    }
    if (item.type === 'enterprise') {
      window.location.href = buildUrlWithParameters(getEnterpriseUrl(item), {
        source: 'autocomplete_result',
      });
      return;
    }
    if (item.type === 'law_firm') {
      window.location.href = buildUrlWithParameters(getLawFirmUrl(item), {
        source: 'autocomplete_result',
      });
      return;
    }
    if (item.type === 'legislation') {
      window.location.href = buildUrlWithParameters(
        getLegislationUrl(
          item.metadata.leg_id,
          item.metadata.legislation_type,
          item.metadata.inner_type,
        ),
        {
          source: 'autocomplete_result',
        },
      );
      return;
    }
    this.selectItem(item, 'autocomplete');
  };

  selectItem = (
    item: AutocompleteItemType | WrittenQueryItemType,
    source: string,
  ) => {
    this.setState({ index: -1 });
    this.reportAutocompleteSelected(item);
    this.props.onItemSelected(item.label, source);
  };

  getItemType = (
    item: AutocompleteItemType | WrittenQueryItemType,
  ):
    | 'example'
    | 'history'
    | 'lawyer'
    | 'suggested_text'
    | 'enterprise'
    | 'written_query'
    | 'legislation'
    | 'law_firm' => {
    const { isHistoryMode, isDemoMode } = this.props;

    if (isDemoMode) {
      return 'example';
    }

    if (isHistoryMode) {
      return 'history';
    }

    if (item.type === 'query') {
      return 'suggested_text';
    }
    return item.type;
  };

  reportAutocompleteSelected = (
    item: AutocompleteItemType | WrittenQueryItemType,
    type?: string,
  ) => {
    const { items, query } = this.props;
    const itemType = type || this.getItemType(item);

    const analyticData = {
      actual_query: query,
      autocomplete_array: items.map(({ label }) => label),
      autocomplete_suggestions: items,
      type: itemType,
      new_query: item.label,
    };
    doctrineAnalytics('Search AutocompleteItemSelected', analyticData);
  };

  onLawyerClicked = (item: AutocompleteItemType) => () => {
    const { manualClose } = this.props;
    this.reportAutocompleteSelected(item, 'lawyer_filter');
    manualClose();
  };

  reportClick = (index: number) => () => {
    const { items, query, isDemoMode } = this.props;

    const item = getSelectedItem(query, items, index, isDemoMode);
    this.reportAutocompleteSelected(item);
  };

  render() {
    const { items, query, isDemoMode } = this.props;
    const { index } = this.state;
    const displayedItems = isDemoMode ? autocompleteDemoItems : items;
    const offset = query.length > 0 ? 1 : 0;
    let isFirstEntity = true;
    const selectedIndex = index;

    return (
      <ul>
        {isDemoMode && (
          <li className="search-bar-dropdown__title">
            <FormattedMessage id="search/components/search-bar/dropdown/SearchAutocompleteList.tsx/1" />
          </li>
        )}
        {query.length > 0 && (
          <AutocompleteQueryItem
            onClick={this.onMouseSelect(0)}
            key={`user---${query}`}
            item={query}
            label={query}
            selected={selectedIndex === 0}
          />
        )}
        {displayedItems.map((item, i: number) => {
          const updateIndex = i + offset;
          const isEntity = [
            'lawyer',
            'enterprise',
            'law_firm',
            'legislation',
          ].includes(item.type);
          const hasOffset = isFirstEntity && i > 0 && isEntity;
          if (isEntity) {
            isFirstEntity = false;
          }

          if (item.type === 'query') {
            return (
              <AutocompleteQueryItem
                item={item.suggestion}
                onClick={this.onMouseSelect(updateIndex)}
                key={item.suggestion}
                label={item.label}
                selected={updateIndex === selectedIndex}
              />
            );
          }
          if (item.type === 'lawyer') {
            return (
              <AutocompleteLawyerItem
                hasOffset={hasOffset}
                item={item}
                key={item.metadata.lawyer_id}
                label={item.label}
                selected={updateIndex === selectedIndex}
                onClick={this.reportClick(updateIndex)}
                onSecondaryClick={this.onLawyerClicked(item)}
              />
            );
          }
          if (item.type === 'enterprise') {
            return (
              <AutocompleteEnterpriseItem
                hasOffset={hasOffset}
                item={item}
                key={item.metadata.enterprise_id}
                label={item.label}
                selected={updateIndex === selectedIndex}
                onClick={this.reportClick(updateIndex)}
              />
            );
          }
          if (item.type === 'law_firm') {
            return (
              <AutocompleteLawFirmItem
                hasOffset={hasOffset}
                key={item.metadata.law_firm_id}
                item={item}
                label={item.label}
                selected={updateIndex === selectedIndex}
                onClick={this.reportClick(updateIndex)}
              />
            );
          }
          if (item.type === 'legislation') {
            return (
              <AutocompleteLegislationItem
                hasOffset={hasOffset}
                key={item.metadata.leg_id}
                item={item}
                label={item.label}
                selected={updateIndex === selectedIndex}
                onClick={this.reportClick(updateIndex)}
              />
            );
          }
          return null;
        })}
      </ul>
    );
  }
}
